import { clientEnv } from '@retailer-platform/dashboard/env'
import { trackAction } from '@retailer-platform/dashboard/analytics'

// ---------------------------------------------------------------------------
// Instrumented fetch wrapper for cx-admin legacy calls
// ---------------------------------------------------------------------------

/**
 * Normalizes a cx-admin URL pathname into a stable Datadog RUM action name by
 * replacing dynamic path segments (numeric IDs) with `:id`.
 *
 * Any new endpoint added to this file is automatically captured — no label
 * registration required.
 *
 * Examples:
 *   /admin/orders/12345/cancel          → /admin/orders/:id/cancel
 *   /admin/order_deliveries/67890.json  → /admin/order_deliveries/:id.json
 *   /admin/care/customers/111/order/222/item_issues.json
 *     → /admin/care/customers/:id/order/:id/item_issues.json
 */
export function normalizeCxAdminPath(url: string): string {
  try {
    const { pathname } = new URL(url)
    return pathname.replace(/\/\d+/g, '/:id')
  } catch {
    return url
  }
}

/**
 * Drop-in replacement for `fetch()` in cx-admin calls. Measures wall-clock
 * duration and emits a `cx_admin.api_call` Datadog RUM action with:
 *   - endpoint: normalized pathname (IDs replaced with `:id`)
 *   - outcome: `success` (2xx) or `error` (non-2xx / network failure)
 *   - http_status: response status code (null on network error)
 *   - duration_ms: client-observed wall-clock time
 *   - method: HTTP verb
 *
 * Returns a standard `Promise<Response>` — callers use it exactly like `fetch()`.
 * Network errors are reported automatically before re-throwing.
 */
export async function cxAdminFetch(input: string, init?: RequestInit): Promise<Response> {
  const endpoint = normalizeCxAdminPath(input)
  const method = init?.method ?? 'GET'
  const start = performance.now()

  let res: Response
  try {
    res = await fetch(input, init)
  } catch (err) {
    trackAction('cx_admin.api_call', {
      endpoint,
      outcome: 'error',
      http_status: null,
      duration_ms: Math.round(performance.now() - start),
      method,
    })
    throw err
  }

  trackAction('cx_admin.api_call', {
    endpoint,
    outcome: res.ok ? 'success' : 'error',
    http_status: res.status,
    duration_ms: Math.round(performance.now() - start),
    method,
  })

  return res
}

// ---------------------------------------------------------------------------

// Module-level variable — JS modules are evaluated once per page load, so this
// persists across calls for the lifetime of the page session. Cleared on 422 so
// the next attempt re-fetches.
let cachedCsrfToken: string | null = null

async function fetchCxAdminCsrfToken(): Promise<string | null> {
  if (cachedCsrfToken) return cachedCsrfToken

  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl) return null

  try {
    const url = new URL('/admin', baseUrl)
    const res = await fetch(url.toString(), { credentials: 'include', mode: 'cors' })
    if (!res.ok) return null

    const html = await res.text()
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const token = doc.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? null
    cachedCsrfToken = token
    return token
  } catch {
    return null
  }
}

// Legacy care controllers return validation failures as a `{ errors: [...] }` JSON body. Try to
// surface those messages; return null when the body isn't that shape (e.g. an HTML CSRF error page)
// so the caller can fall back to its own handling.
async function readCxAdminErrors(res: Response): Promise<string | null> {
  try {
    const json = await res.json()
    const errors = json?.errors
    if (Array.isArray(errors) && errors.length > 0) return errors.join(', ')
    return null
  } catch {
    return null
  }
}

export async function submitMoveCharges({
  orderDeliveryId,
  creditCardId,
}: {
  orderDeliveryId: string
  creditCardId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  // The `.json` extension forces the controller's `format.json` branch, which renders the outcome as
  // `{ success, message }`. Without it the request takes `format.html`, which 302-redirects on BOTH
  // success and failure; `fetch` follows the redirect and the resolved response reflects that landing
  // page rather than the move outcome (the source bug). The `Accept` header alone does not switch the
  // format for this controller.
  const url = new URL('/admin/charge_logs/move_charges.json', baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'PUT',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrfToken,
    },
    body: new URLSearchParams({
      chargeable_id: orderDeliveryId,
      chargeable_type: 'OrderDelivery',
      credit_card_id: creditCardId,
    }),
  })

  // The json branch returns 200 with `{ success, message }` on both paths, so the body — not the
  // status — decides the outcome. Parse first (like rescheduleOrderDelivery) so a 422 carrying a
  // real message isn't misreported as an expired session.
  let json: { success?: unknown; message?: unknown } | null = null
  try {
    json = (await res.json()) as { success?: unknown; message?: unknown }
  } catch {
    // No parseable JSON body (e.g. an HTML CSRF error page).
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    // Rails' protect_from_forgery with: :exception raises ActionController::InvalidAuthenticityToken
    // on a bad/expired CSRF token, which the Rails exception wrapper maps to 422 with an HTML body.
    // Clear the cached token so the next attempt re-fetches a fresh one.
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(
      typeof json?.message === 'string' ? json.message : `Request failed (${res.status})`
    )
  }

  // Throw on failure so the caller's catch surfaces the server message as an error toast; resolve
  // on success for the success toast.
  if (!json || typeof json.success !== 'boolean') {
    throw new Error('Move charges failed: unexpected response')
  }
  if (!json.success) {
    throw new Error(typeof json.message === 'string' ? json.message : 'Move charges failed')
  }
}

// PUT /admin/order_deliveries/:id — moves the delivery to an existing address id. Success returns
// `{ success: true, order_delivery }` (200); a blocked/disallowed change returns `{ errors }` (422).
export async function updateOrderDeliveryAddress({
  orderDeliveryId,
  addressId,
}: {
  orderDeliveryId: string
  addressId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  if (!orderDeliveryId) throw new Error('Missing order delivery id')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  // The `.json` extension forces the controller's `format.json` branch (the `Accept` header alone is
  // ignored).
  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}.json`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({
      _method: 'put',
      authenticity_token: csrfToken,
      'order_delivery[address_id]': addressId,
    }),
  })

  if (res.status === 422) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }
}

export interface ModifyOrderReason {
  reason: string
  display: string
  presetFee: boolean
  feeExempt: boolean
}

export type ModificationRequester = 'customer' | 'retailer' | 'shopper' | 'fair_team'

// v3 keys its per-requester map by the uppercase constant from
// InstaAdmin::Care::ModifyOrderService (SHOPPER/CUSTOMER/RETAILER/FAIR_TEAM).
type V3RequesterKey = 'SHOPPER' | 'CUSTOMER' | 'RETAILER' | 'FAIR_TEAM'

interface ModifyOrderReasonsResponse {
  enabled?: boolean
  per_requester_type: Partial<Record<V3RequesterKey, ModifyOrderReason[]>>
  default: ModifyOrderReason[]
}

export async function fetchModifyOrderReasons({
  customerId,
  orderId,
}: {
  customerId: string
  orderId: string
}): Promise<{
  enabled: boolean
  perRequesterType: Record<ModificationRequester, ModifyOrderReason[]>
  default: ModifyOrderReason[]
}> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(
    `/admin/care/customers/${customerId}/order/${orderId}/modify_order_reasons.json`,
    baseUrl
  )
  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }

  const json = (await res.json()) as ModifyOrderReasonsResponse

  return {
    // Default to true so an older v3 build (without the `enabled` field) doesn't
    // silently hide the menu item.
    enabled: json.enabled ?? true,
    perRequesterType: {
      customer: json.per_requester_type.CUSTOMER ?? [],
      retailer: json.per_requester_type.RETAILER ?? [],
      shopper: json.per_requester_type.SHOPPER ?? [],
      fair_team: json.per_requester_type.FAIR_TEAM ?? [],
    },
    default: json.default,
  }
}

// Keys returned by move_charges_restrictions / add_charge_restrictions.
// Kept as string union rather than a strict enum so a new v3 restriction key
// doesn't break the JSON deserialization — new keys will surface in tooltips as-is.
export type ChargeButtonRestriction = string

export interface ChargeButtonRestrictionsResult {
  enabled: boolean
  restrictions: ChargeButtonRestriction[]
  // move_charges_restrictions only: v3's OrderDelivery#can_reconcile? for the order's delivery.
  // Undefined from a v3 build without the field, which keeps the reconcile button hidden.
  canReconcile?: boolean
}

interface ChargeButtonRestrictionsResponse {
  enabled?: boolean
  restrictions?: ChargeButtonRestriction[]
  can_reconcile?: boolean
}

async function fetchChargeButtonRestrictions(
  path: string,
  { customerId, orderId }: { customerId: string; orderId: string }
): Promise<ChargeButtonRestrictionsResult> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(`/admin/care/customers/${customerId}/order/${orderId}/${path}`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }

  const json = (await res.json()) as ChargeButtonRestrictionsResponse
  const restrictions = Array.isArray(json.restrictions) ? json.restrictions : []
  return {
    // Default to true so an older v3 build (without the `enabled` field) doesn't
    // silently disable the button.
    enabled: json.enabled ?? true,
    restrictions,
    canReconcile: json.can_reconcile,
  }
}

export const fetchMoveChargesRestrictions = (params: {
  customerId: string
  orderId: string
}): Promise<ChargeButtonRestrictionsResult> =>
  fetchChargeButtonRestrictions('move_charges_restrictions', params)

export const fetchAddChargeRestrictions = (params: {
  customerId: string
  orderId: string
}): Promise<ChargeButtonRestrictionsResult> =>
  fetchChargeButtonRestrictions('add_charge_restrictions', params)

export type ModifyOrderAction = 'CANCEL' | 'RESCHEDULE' | 'UNASSIGN' | 'COMPLETE'

// Mirrors InstaAdmin::Care::ModifyOrderService::REQUESTED_BY_VALUES.
const REQUESTER_TO_V3: Record<ModificationRequester, string> = {
  customer: 'CUSTOMER',
  retailer: 'RETAILER',
  shopper: 'SHOPPER',
  fair_team: 'FAIR_TEAM',
}

// Mirrors the RetailerPolicies proto enum used by
// InstaAdmin::Care::ModifyOrderService.evaluate_care_order_policy. Any string
// v3 returns for `decision` that isn't one of these is treated as no-op by our
// UI (and by v3's own `emit_care_order_policy_metric`, which falls back to
// `:allowed`).
export type CareOrderPolicyDecision =
  | 'CARE_POLICY_DECISION_WARNED'
  | 'CARE_POLICY_DECISION_BLOCKED'
  | 'CARE_POLICY_DECISION_HARD_BLOCKED'

export interface CareOrderPolicyEntry {
  decision: CareOrderPolicyDecision | string
  explanation: string | null
}

export interface CareOrderPolicy {
  cancellation_policy?: CareOrderPolicyEntry | null
  reschedule_policy?: CareOrderPolicyEntry | null
}

export interface ModifyOrderOptionsResult {
  modifyOptions: ModifyOrderAction[]
  presetFee: boolean
  feeExempt: boolean
  feeExemptReason: string
  canOverrideBlockedPolicy: boolean
  restrictionReason: string | null
  careOrderPolicy: CareOrderPolicy | null
}

interface ModifyOrderOptionsResponse {
  modify_options: ModifyOrderAction[]
  preset_fee: boolean
  fee_exempt: boolean
  fee_exempt_reason: string
  can_override_blocked_policy: boolean
  restriction_reason: string | null
  care_order_policy: CareOrderPolicy | null
}

export async function fetchModifyOrderOptions({
  customerId,
  orderId,
  orderDeliveryId,
  requestedBy,
  reason,
}: {
  customerId: string
  orderId: string
  orderDeliveryId: string
  requestedBy: ModificationRequester
  reason: string
}): Promise<ModifyOrderOptionsResult> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(
    `/admin/care/customers/${customerId}/order/${orderId}/modify_order_options`,
    baseUrl
  )
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      requested_by: REQUESTER_TO_V3[requestedBy],
      reason,
      delivery_id: orderDeliveryId,
    }),
  })

  if (res.status === 422) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }

  const json = (await res.json()) as ModifyOrderOptionsResponse

  return {
    modifyOptions: json.modify_options,
    presetFee: json.preset_fee,
    feeExempt: json.fee_exempt,
    feeExemptReason: json.fee_exempt_reason,
    canOverrideBlockedPolicy: json.can_override_blocked_policy,
    restrictionReason: json.restriction_reason,
    careOrderPolicy: json.care_order_policy ?? null,
  }
}

export interface CancelOrderResult {
  success: boolean
  message: string
}

// Matches v3's flash notice from
// insta_admin/orders_controller.rb#cancel_order for orders with 100+ items:
// "This cancellation may take a while, we will work on it and email
// <email> when it's finished".
export function isAsyncCancelMessage(message: string): boolean {
  return /may take a while/i.test(message)
}

export async function cancelOrder({
  orderId,
  orderDeliveryId,
  requestedBy,
  reason,
  ticketId,
  description,
  chargeExtraFee,
  feeAmountCents,
  policyDecision,
}: {
  orderId: string
  orderDeliveryId: string
  requestedBy: ModificationRequester
  reason: string
  ticketId: string
  description: string
  chargeExtraFee: boolean
  feeAmountCents: number
  policyDecision?: string | null
}): Promise<CancelOrderResult> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  // v3's ModificationConfirmation posts a normal HTML form to /admin/orders/:id/cancel
  // with _method=put. We POST the same form but ask for JSON back so we can
  // distinguish the sync-success, async-enqueued, and failure paths (the HTML
  // path returns a 302 with a flash notice, which we can't read).
  const url = new URL(`/admin/orders/${orderId}/cancel`, baseUrl)
  const body = new URLSearchParams({
    _method: 'put',
    reason,
    description,
    requested_by: REQUESTER_TO_V3[requestedBy],
    ticket_id: ticketId,
    // v3 sends "all" when cancelling the whole order; a specific delivery id
    // cancels only that leg. IPP's current flow cancels the whole order.
    delivery_id: orderDeliveryId || 'all',
    charge_extra_fee: chargeExtraFee ? 'true' : 'false',
    fee_amount_cents: String(feeAmountCents),
  })
  if (policyDecision) body.set('policy_decision', policyDecision)

  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  // v3 returns 200 for success and 422 with a JSON body for the DTC/duplicate
  // failure paths. Rails also raises 422 for InvalidAuthenticityToken (bad/expired
  // CSRF), but that path renders no parseable JSON — parse the body first so a
  // real business error isn't misreported as "Session expired" (and doesn't wipe
  // the cached CSRF token, forcing an unnecessary re-fetch on the next call).
  let json: Partial<CancelOrderResult> | null = null
  try {
    json = (await res.json()) as Partial<CancelOrderResult>
  } catch {
    // no parseable body
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(json?.message ?? `Cancel failed (${res.status})`)
  }
  if (!json || typeof json.success !== 'boolean' || typeof json.message !== 'string') {
    throw new Error('Cancel failed: unexpected response')
  }
  return { success: json.success, message: json.message }
}

export async function submitAddCharge({
  orderDeliveryId,
  amount,
  reason,
  feeType,
}: {
  orderDeliveryId: string
  amount: number
  reason: string
  feeType?: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  // `.json` forces the `format.json` branch ({ success, message }); without it `format.html`
  // 302-redirects on success too, and the followed HTML page makes res.json() throw, surfacing
  // success as a failure toast (the bug). CSRF rides in authenticity_token to stay CORS-simple.
  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/add_charge.json`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({
      authenticity_token: csrfToken,
      charge_amount: String(Math.abs(amount)),
      reason,
      ...(feeType ? { fee_type: feeType } : {}),
    }),
  })

  // Body, not status, decides the outcome. Parse first so a 422 with a real message isn't misread
  // as an expired session.
  let json: { success?: unknown; message?: unknown } | null = null
  try {
    json = (await res.json()) as { success?: unknown; message?: unknown }
  } catch {
    // No JSON body (e.g. a followed HTML redirect).
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    // Bad/expired CSRF token: Rails raises InvalidAuthenticityToken (422, HTML). Re-fetch next time.
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(
      typeof json?.message === 'string' ? json.message : `Request failed (${res.status})`
    )
  }

  // Throw on failure so the caller's catch shows the server message; resolve on success.
  if (!json || typeof json.success !== 'boolean') {
    throw new Error('Add charge failed: unexpected response')
  }
  if (!json.success) {
    throw new Error(
      typeof json.message === 'string' ? json.message : 'Charge failed. Please try again.'
    )
  }
}

// POST /admin/order_deliveries/:id/reconcile.json (legacy "Attempt completion reconciliation"),
// shaped like submitAddCharge. Resolves with the server message; throws it when `success` is false.
export async function submitCompletionReconciliation({
  orderDeliveryId,
}: {
  orderDeliveryId: string
}): Promise<string> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/reconcile.json`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({ authenticity_token: csrfToken }),
  })

  let json: { success?: unknown; message?: unknown } | null = null
  try {
    json = (await res.json()) as { success?: unknown; message?: unknown }
  } catch {
    // No JSON body (e.g. a followed HTML redirect).
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(
      typeof json?.message === 'string' ? json.message : `Request failed (${res.status})`
    )
  }

  if (!json || typeof json.success !== 'boolean' || typeof json.message !== 'string') {
    throw new Error('Completion reconciliation failed: unexpected response')
  }
  if (!json.success) throw new Error(json.message)
  return json.message
}

// PUT /admin/customers/:id — updates one entry on the customer's `settings` blob. Rails'
// InstaAdmin::CustomersController#update reads bracket-nested params of the form
// `settings[<name>][type]=<type>&settings[<name>][value]=<value>`. The controller special-cases
// `type=boolean` (`.to_bool`) and `type=array` (split by comma); anything else stores the raw string.
// Returns `{ success: true, settings }` on 200 and `{ errors: [...] }` on 422.
type CustomerSettingUpdate =
  | { name: string; type: 'boolean'; value: boolean }
  | { name: string; type: 'string'; value: string }
  // `type=array` is sent as a comma-separated string; the controller runs `value.split(',').map(&:to_i)`.
  | { name: string; type: 'array'; value: string }

// Rails `Preferences.get_all` returns the raw hash — booleans stay booleans, strings stay strings.
// Values are unknown at compile time; callers must narrow per key.
export type CustomerSettings = Record<string, unknown>

// GET-shaped fetch built on top of the same update endpoint: PUT with no `customer`/`settings`
// params skips every mutation branch and falls through to the JSON responder, which renders
// `{ success: true, settings: @customer.settings.get_all }`. No dedicated read endpoint exists on
// the legacy admin, so this is the pragmatic way to read the current settings blob.
export async function fetchCustomerSettings({
  customerId,
}: {
  customerId: string
}): Promise<CustomerSettings> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/customers/${customerId}`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'PUT',
    credentials: 'include',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
  })

  if (res.status === 422) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }
  if (!res.ok) throw new Error(`Request failed (${res.status})`)

  const json = (await res.json()) as { settings?: CustomerSettings }
  return json.settings ?? {}
}

export async function updateCustomerSetting({
  customerId,
  update,
}: {
  customerId: string
  update: CustomerSettingUpdate
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/customers/${customerId}`, baseUrl)
  const serializedValue =
    update.type === 'boolean' ? (update.value ? 'true' : 'false') : update.value
  const body = new URLSearchParams({
    [`settings[${update.name}][type]`]: update.type,
    [`settings[${update.name}][value]`]: serializedValue,
  })

  const res = await cxAdminFetch(url.toString(), {
    method: 'PUT',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  // Same 422 pattern as cancelOrder/rescheduleOrderDelivery: parse first so a real validation error
  // (JSON `{ errors }`) isn't misreported as "Session expired" (an HTML CSRF error page).
  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

export interface ToggleActivationResult {
  success: boolean
  active: boolean
  message: string
}

// POSTs to `InstaAdmin::CustomersController#toggle_activation` with Accept: application/json.
// The controller flips the user's active flag (deactivates active users, reactivates inactive ones),
// records a Note, and returns `{ success, active, message }` — 200 on success, 403 on BPO-corporate
// block, 422 on reactivate failure.
export async function submitToggleActivation({
  customerId,
}: {
  customerId: string
}): Promise<ToggleActivationResult> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/customers/${customerId}/toggle_activation`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
  })

  // Same pattern as cancelOrder: 422 is either a validation error (parseable JSON) or expired CSRF
  // (HTML). Parse first so a real business error isn't misreported as "Session expired".
  let json: Partial<ToggleActivationResult> | null = null
  try {
    json = (await res.json()) as Partial<ToggleActivationResult>
  } catch {
    // no parseable body
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(json?.message ?? `Toggle activation failed (${res.status})`)
  }
  if (!json || typeof json.success !== 'boolean' || typeof json.message !== 'string') {
    throw new Error('Toggle activation failed: unexpected response')
  }
  return { success: json.success, active: Boolean(json.active), message: json.message }
}

export async function removeChatMessageImage({
  customerId,
  chatMessageId,
}: {
  customerId: string
  chatMessageId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/care/customers/${customerId}/remove_chat_message_image`, baseUrl)
  url.searchParams.set('chat_message_id', chatMessageId)

  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
  })

  if (res.status === 422) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    // The legacy controller returns 500 with `{ error: "..." }` when the downstream RPC fails; try
    // to surface that message. 403 (EU Costco) and 404 (feature flag off) have empty bodies.
    let message: string | null = null
    try {
      const json = await res.json()
      if (typeof json?.error === 'string') message = json.error
    } catch {
      // no parseable body
    }
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// GET /admin/care/customers/:customer_id/order/:order_id/order_resolution_summary — returns the
// resolution/appeasement confirmation email body as raw HTML (Rails `render :html`, no layout). The
// preview modal renders this inside a sandboxed iframe. Safe GET, so no CSRF token is required.
// This is the only cx-admin transport that negotiates HTML (Accept: text/html) rather than JSON, so
// it's the only one InstaAdmin::IppRedirector's `eligible_for_redirect?` (GET + html format) can
// intercept with a cross-origin 302 for users on the ipp redirect rollout — embedded_in_ipp=true
// opts this request out, same as the iframe src builder in urlBuilder.ts.
export async function getAppeasementEmailPreview({
  customerId,
  orderId,
}: {
  customerId: string
  orderId: string
}): Promise<string> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(
    `/admin/care/customers/${customerId}/order/${orderId}/order_resolution_summary`,
    baseUrl
  )
  url.searchParams.set('embedded_in_ipp', 'true')
  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: { Accept: 'text/html' },
  })

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }

  return res.text()
}

// POST /admin/care/customers/:customer_id/order/:order_id/send_appeasement_email — enqueues the
// resolution confirmation email (Care::SendAppeasementEmailJob) keyed off order_id + current_user,
// and returns `"ok"`. The downstream send path dedupes (Growth::SendEmail max_times: 1), so a
// redundant send after an auto-send is a no-op. Sent CORS-simple (authenticity_token in the body,
// no custom headers) to avoid a credentialed preflight.
export async function sendAppeasementEmail({
  customerId,
  orderId,
}: {
  customerId: string
  orderId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(
    `/admin/care/customers/${customerId}/order/${orderId}/send_appeasement_email`,
    baseUrl
  )
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({
      authenticity_token: csrfToken,
    }),
  })

  if (res.status === 422) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }
}

// UI-origin tag the legacy "Resolve delivery issue" care page records for auditing.
const RESOLVE_DELIVERY_ISSUE_UI_ORIGIN = 'care_admin_resolve_delivery_issues'

// `credit`/`refund` post to the unified care_create endpoint; `redelivery` uses its own
// redelivery_options/redelivery endpoints with a per-item cart (see below).
export type DeliveryIssueResolution = 'credit' | 'refund' | 'redelivery'

export interface OrderRefundableCharges {
  fullRefund: {
    // Whether a whole-delivery refund to the original payment method is permitted.
    allowed: boolean
    // Pre-formatted amount string from the legacy API (e.g. "$33.89"), or null if unavailable.
    amount: string | null
  }
  // Numeric order-delivery id parsed from the API's care_refund link
  // (e.g. "/admin/order_deliveries/123/care_refund" -> "123"). Null when unavailable.
  orderDeliveryId: string | null
  // Balance still owed on the delivery (total charged minus total collected), clamped to zero.
  // Pre-formatted string (e.g. "$10.00") from the server; null when the field is absent (older builds).
  unpaidAmount: { amountCents: number; amount: string } | null
}

// The care charges API embeds the order-delivery id in the refund link; care_create needs it
// explicitly, so pull it out of the path.
function parseOrderDeliveryId(refundPath: string | null): string | null {
  if (!refundPath) return null
  const match = refundPath.match(/order_deliveries\/([^/]+)\//)
  return match?.[1] ?? null
}

// GET request (safe verb), so no CSRF token is required.
export async function getOrderRefundableCharges({
  orderId,
  customerId,
}: {
  orderId: string
  customerId: string
}): Promise<OrderRefundableCharges> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(`/admin/api/v1/care/orders/${orderId}/charges`, baseUrl)
  url.searchParams.set('customer_id', customerId)

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  })

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }

  const json = await res.json()
  const fullRefund = json?.refunds?.fullRefund ?? {}
  const unpaid = json?.unpaidAmount
  return {
    fullRefund: {
      allowed: Boolean(fullRefund.allowed),
      amount: fullRefund.amount ?? null,
    },
    orderDeliveryId: parseOrderDeliveryId(json?.links?.refund ?? null),
    unpaidAmount:
      unpaid != null && typeof unpaid.amountCents === 'number'
        ? { amountCents: unpaid.amountCents, amount: String(unpaid.amount ?? '') }
        : null,
  }
}

// POST a delivery issue and its resolution to the unified legacy `order_issues#care_create`
// endpoint, which records the OrderIssue and applies the resolution in one call. `resolution` is
// omitted for issue types that carry none (e.g. Customer MIA). Sent as Rails bracket-nested form
// fields, not JSON, so ParamsWrapper can't clobber the `order_issue` nesting.
export async function submitResolveDeliveryIssue({
  orderId,
  customerId,
  orderDeliveryId,
  orderIssueTypeId,
  resolution,
  caseNumber,
}: {
  orderId: string
  customerId: string
  orderDeliveryId: string
  orderIssueTypeId: number
  resolution?: DeliveryIssueResolution
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const body = new URLSearchParams({
    'order_issue[order_id]': orderId,
    'order_issue[user_id]': customerId,
    'order_issue[order_delivery_id]': orderDeliveryId,
    'order_issue[order_issue_type_id]': String(orderIssueTypeId),
    'order_issue[ui_origin]': RESOLVE_DELIVERY_ISSUE_UI_ORIGIN,
    case_number: caseNumber,
    // Credit reads this raw top-level `ticket_id` for attribution (refund instead derives its ticket
    // from `case_number` server-side), so send the validated case here too — otherwise credits are
    // recorded with a nil ticket. Safe for every resolution: it's the same case reference.
    ticket_id: caseNumber,
    ui_origin: RESOLVE_DELIVERY_ISSUE_UI_ORIGIN,
  })
  if (resolution) body.set('appeasement_type', resolution)

  const url = new URL('/admin/order_issues/care_create', baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  if (res.status === 422) {
    // 422 is ambiguous here: it's either a controller validation error (JSON `{errors}`, e.g. an
    // invalid case number) or an expired CSRF token (an HTML error page). Prefer surfacing the
    // validation message; otherwise clear the cached token so the next attempt re-fetches.
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// Mirrors InstaAdmin::OrdersHelper::REDELIVERY_REASONS (no IPP endpoint exposes it); submitted verbatim.
export const REDELIVERY_REASONS = [
  'customer mia',
  'duplicate order',
  'too many out of stock items',
  'single item order out of stock',
  'customer requested',
  'unbatchable',
  'fraud',
  'other',
] as const

export type RedeliveryReason = (typeof REDELIVERY_REASONS)[number]

export interface RedeliveryOption {
  id: string // submitted back as delivery_option_id
  displayName: string
  disabled: boolean
}

export interface RedeliveryCartItem {
  // Rails OrderItem.id as an exact digit string; see submitRedelivery for how it reaches the wire.
  orderItemId: string
  useReplacement: boolean
  qty: number
}

// Redelivery failures come back as `{ meta: { error_message } }` or `{ errors: [...] }`; null if neither.
async function readRedeliveryError(res: Response): Promise<string | null> {
  try {
    const json = await res.json()
    const metaMessage = json?.meta?.error_message
    if (typeof metaMessage === 'string' && metaMessage.length > 0) return metaMessage
    const errors = json?.errors
    if (Array.isArray(errors) && errors.length > 0) return errors.join(', ')
    return null
  } catch {
    return null
  }
}

// Each option comes wrapped in a `{ table: {...} }` object (Rails OpenStruct serialization).
interface RedeliveryOptionRow {
  table?: {
    id?: number | string
    display_name?: string
    disabled?: boolean
  }
}

// GET delivery-time options for a redelivery. Options are delivery-level, quoted with one order item id.
export async function getRedeliveryOptions({
  orderId,
  orderItemId,
}: {
  orderId: string
  orderItemId: string
}): Promise<RedeliveryOption[]> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(`/admin/orders/${orderId}/redelivery_options`, baseUrl)
  url.searchParams.set('order_item_id', orderItemId)

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    // Mark as XHR/JSON (as the legacy jQuery client did) or the controller negotiates HTML and its
    // before_actions 302-redirect the GET to the care page instead of rendering JSON.
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!res.ok) {
    const message = await readRedeliveryError(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }

  const json = (await res.json()) as unknown
  const rows: RedeliveryOptionRow[] = Array.isArray(json) ? json : []
  return rows.flatMap(row => {
    const table = row.table
    if (!table || table.id == null) return []
    return [
      {
        id: String(table.id),
        displayName: table.display_name ?? String(table.id),
        disabled: Boolean(table.disabled),
      },
    ]
  })
}

export interface RescheduleDeliveryOption {
  id: string
  displayName: string
  disabled: boolean
}

export interface RescheduleDeliveryOptionsResult {
  options: RescheduleDeliveryOption[]
  showAlcoholWarning: boolean
}

interface RescheduleDeliveryOptionsResponse {
  delivery_options?: RedeliveryOptionRow[]
  show_alcohol_warning?: boolean
}

// GET available delivery windows for rescheduling an order. The legacy admin returns each option as
// OpenStruct-wrapped `{ table: { id, display_name, disabled } }`; `display_name` is pre-formatted
// (e.g. "Fri 8am–10am · $5.99"), no raw start/end/price fields are exposed.
export async function fetchRescheduleDeliveryOptions({
  customerId,
  orderId,
}: {
  customerId: string
  orderId: string
}): Promise<RescheduleDeliveryOptionsResult> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(
    `/admin/care/customers/${customerId}/order/${orderId}/reschedule_order_data`,
    baseUrl
  )
  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!res.ok) {
    throw new Error(`Request failed (${res.status})`)
  }

  const json = (await res.json()) as RescheduleDeliveryOptionsResponse
  const rows = Array.isArray(json.delivery_options) ? json.delivery_options : []
  const options = rows.flatMap(row => {
    const table = row.table
    if (!table || table.id == null) return []
    return [
      {
        id: String(table.id),
        displayName: table.display_name ?? String(table.id),
        disabled: Boolean(table.disabled),
      },
    ]
  })
  return { options, showAlcoholWarning: Boolean(json.show_alcohol_warning) }
}

export interface RescheduleOrderResult {
  success: boolean
  message: string
}

// POST /admin/order_deliveries/:id/reschedule — mirrors v3's reschedule form submit. Native POST
// (no _method emulation); server returns `{success, message}` JSON when Accept: application/json.
export async function rescheduleOrderDelivery({
  orderDeliveryId,
  deliveryOptionId,
  requestedBy,
  reason,
  description,
  notifyUser,
  resetPicking,
  policyDecision,
}: {
  orderDeliveryId: string
  deliveryOptionId: string
  requestedBy: ModificationRequester
  reason: string
  description: string
  notifyUser: boolean
  resetPicking: boolean
  policyDecision?: string | null
}): Promise<RescheduleOrderResult> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/reschedule`, baseUrl)
  const body = new URLSearchParams({
    delivery_option_id: deliveryOptionId,
    reason,
    description,
    requested_by: REQUESTER_TO_V3[requestedBy],
    notify_user: notifyUser ? 'true' : 'false',
    reset_picking: resetPicking ? 'true' : 'false',
  })
  if (policyDecision) body.set('policy_decision', policyDecision)

  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  // Same pattern as cancelOrder: 422 is either a validation error (parseable JSON) or expired CSRF
  // (HTML). Parse first so a real business error isn't misreported as "Session expired".
  let json: Partial<RescheduleOrderResult> | null = null
  try {
    json = (await res.json()) as Partial<RescheduleOrderResult>
  } catch {
    // no parseable body
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(json?.message ?? `Reschedule failed (${res.status})`)
  }
  if (!json || typeof json.success !== 'boolean' || typeof json.message !== 'string') {
    throw new Error('Reschedule failed: unexpected response')
  }
  return { success: json.success, message: json.message }
}

// POST an order redelivery via its dedicated endpoint (not care_create): a per-item cart, one delivery
// option, a reason, and a top-level order_issue_type_id. JSON so Rails parses the nested arrays.
export async function submitRedelivery({
  orderId,
  cart,
  deliveryOptionId,
  reason,
  orderIssueTypeId,
  caseNumber,
}: {
  orderId: string
  cart: RedeliveryCartItem[]
  deliveryOptionId: string
  reason: string
  orderIssueTypeId: number
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  // order_item_id / delivery_option_id must land as exact, unquoted JSON numbers. Not JS numbers: ids
  // are snowflakes > 2^53 that Number() would corrupt. Not JSON strings either: a string order_item_id
  // makes the controller run recast_cart_values -> qty.to_i, truncating fractional looseweight qty to 0.
  // So stringify a placeholder, then unquote it.
  const exactIntPlaceholders = new Map<string, string>()
  const asExactInt = (rawDigits: string): string => {
    const token = `__EXACT_INT_${exactIntPlaceholders.size}__`
    exactIntPlaceholders.set(token, rawDigits)
    return token
  }

  let body = JSON.stringify({
    id: orderId,
    cart: cart.map(item => ({
      order_item_id: asExactInt(item.orderItemId),
      use_replacement: item.useReplacement,
      qty: item.qty,
    })),
    // Top-level order_issue_type_id => controller treats this as delivery-level (no IC+ gate).
    create_order_issues: true,
    deliveries: [{ delivery_option_id: asExactInt(deliveryOptionId), price: 0 }],
    reason,
    description: '',
    order_issue_type_id: orderIssueTypeId,
    // Send both: attribution derives from case_number via grab_ticket_id, else the appeasement lands
    // with no ticket. Mirrors the care_create path.
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: RESOLVE_DELIVERY_ISSUE_UI_ORIGIN,
  })
  for (const [token, rawDigits] of exactIntPlaceholders) {
    body = body.replace(`"${token}"`, rawDigits)
  }

  const url = new URL(`/admin/orders/${orderId}/redelivery`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  if (res.status === 422) {
    // 422 = validation error (JSON) or expired CSRF (HTML). Prefer the message; else re-fetch the token.
    const message = await readRedeliveryError(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readRedeliveryError(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// Load-bearing: the server picks the credit path (Cheddar vs Coupon) from this. Mirrors the legacy FE.
const RESOLVE_ITEM_ISSUE_UI_ORIGIN = 'care_admin_resolve_item_issues'

// Legacy `item_issues` payload (InstaAdmin::Care::ItemIssuesPresenter#props), camelCased server-side.
// Fields are optional; the hook guards each.
export interface ItemIssuesRefundData {
  totalQty?: number
  // Refundable cap, net of prior refunds AND credits.
  refundableQty?: number
  refundQty?: number
  // "<amount> <unit>" e.g. "2.0 units" / "1.5 lb"; drives count-vs-weight.
  label?: string
  totalCharge?: { price?: number; label?: string } | null
  refundReason?: string | null
  // [name, id] server-validated reasons.
  itemRefundReasons?: [string, number][]
  selectedRefundReasonId?: number | null
  refundAmount?: number
  // Scales the refund for POOR_REPLACEMENT; 1.0 otherwise.
  refundFraction?: number
}

export interface ItemIssuesItemDetails {
  displayName?: string
  // Nullable in the real payload (`null`, not `false`, for non-alcohol items).
  isAlcoholic?: boolean | null
  isHsaFsaCreditRestricted?: boolean
  isMedicareCreditRestricted?: boolean
}

export interface ItemIssuesOrderItem {
  // Exact digit string (snowflake > 2^53; quoted in JSON to preserve it).
  orderItemId: string
  status?: string
  checked?: boolean
  // Auto-appeased ("already resolved"): shown for context, not re-resolvable.
  disableAppeasement?: boolean
  autoAppeased?: boolean
  imageUrl?: string | null
  details?: ItemIssuesItemDetails | null
  // `{}` when the item has no final_item — non-refundable.
  refundData?: ItemIssuesRefundData | null
  currency?: string | null
}

export interface ItemIssuesOrderDelivery {
  // Exact digit string (snowflake > 2^53).
  id: string
  creditAvailable?: boolean
  refundAvailable?: boolean
  // Item-level redelivery gate (legacy `itemRedeliveryAvailable`): true only when this delivery
  // supports offering a redelivery of the selected items (IC+ membership + a redeliverable delivery).
  // It is the single correct UI gate for offering item redelivery.
  itemRedeliveryAvailable?: boolean
  orderItems?: (ItemIssuesOrderItem | null)[] | null
}

export interface ItemIssuesResponse {
  orderDeliveries?: (ItemIssuesOrderDelivery | null)[] | null
}

// GET item-level appeasement data. No CSRF (safe verb). The `.json` extension forces JSON (Accept alone can 406).
export async function fetchItemIssues({
  customerId,
  orderId,
}: {
  customerId: string
  orderId: string
}): Promise<ItemIssuesResponse> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(
    `/admin/care/customers/${customerId}/order/${orderId}/item_issues.json`,
    baseUrl
  )
  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }

  return (await res.json()) as ItemIssuesResponse
}

// A single per-item resolution row from the legacy `order_items.json` payload.
// `type` is "Credit" or "Refund"; `total` is a pre-formatted money string (e.g. "$5.00");
// `qty` is a raw number for credits and a formatted string for refunds.
export interface OrderItemResolution {
  source?: string | null
  qty?: number | string | null
  type?: string | null
  reason?: string | null
  total?: string | null
}

// Trusted per-item weight for the "Shopper Wage Weights" block, sourced server-side from
// HeavyPayService. `soldByWeight` true means the item has no meaningful per-unit weight (legacy
// copy: "Item is sold by total weight. No single item weight."); otherwise `singleItemWeightLbs`
// is `totalWeightLbs` spread over the reconciled quantity.
export interface OrderItemWeight {
  totalWeightLbs?: number | null
  soldByWeight?: boolean | null
  singleItemWeightLbs?: number | null
}

// Per-item detail row from the legacy `order_items.json` payload: resolutions (credits/refunds),
// how the item was picked (e.g. "scanner" / "force_button" / "admin"), and its trusted weight.
export interface OrderItemDetails {
  resolutions: OrderItemResolution[]
  pickedVia?: string | null
  weight?: OrderItemWeight | null
}

interface OrderItemsPageItem {
  // The heavy `orderItemCardHTML` sibling blob is intentionally not read.
  orderItem?: {
    // Exact digit string (snowflake > 2^53).
    id?: string | number | null
    resolutions?: (OrderItemResolution | null)[] | null
    pickedVia?: string | null
    weight?: OrderItemWeight | null
  } | null
}

interface OrderItemsPageDelivery {
  orderItems?: (OrderItemsPageItem | null)[] | null
}

interface OrderItemsPageResponse {
  orderDeliveries?: (OrderItemsPageDelivery | null)[] | null
  pageData?: {
    currentPage?: number | null
    nextPage?: number | null
    totalPages?: number | null
  } | null
}

// Loop guard: the legacy endpoint paginates 25/50 items per page.
const MAX_ORDER_ITEM_PAGES = 50

// GET legacy per-item detail data (credits/refunds, pick method, trusted weight), keyed by
// orderItemId. No CSRF (safe verb). The `.json` extension forces JSON (Accept alone can 406).
// Paginates via `pageData.nextPage`; drops the heavy `orderItemCardHTML` blob.
export async function getOrderItemResolutions({
  customerId,
  orderId,
}: {
  customerId: string
  orderId: string
}): Promise<Map<string, OrderItemDetails>> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const itemDetailsByItemId = new Map<string, OrderItemDetails>()
  let page = 1

  for (let pageCount = 0; pageCount < MAX_ORDER_ITEM_PAGES; pageCount++) {
    const url = new URL(`/admin/care/customers/${customerId}/order_items.json`, baseUrl)
    url.searchParams.set('order_id', orderId)
    url.searchParams.set('page', String(page))

    const res = await cxAdminFetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      headers: { Accept: 'application/json' },
    })

    if (!res.ok) {
      const message = await readCxAdminErrors(res)
      throw new Error(message ?? `Request failed (${res.status})`)
    }

    const payload = (await res.json()) as OrderItemsPageResponse

    for (const delivery of payload.orderDeliveries ?? []) {
      for (const entry of delivery?.orderItems ?? []) {
        const item = entry?.orderItem
        if (item?.id == null) continue
        const id = item.id
        const resolutions = (item.resolutions ?? []).filter(
          (resolution): resolution is OrderItemResolution => resolution != null
        )
        // Same item can appear under multiple deliveries with identical data; last write wins.
        // Items with no resolutions are still recorded: pickedVia/weight travel independently.
        itemDetailsByItemId.set(String(id), {
          resolutions,
          pickedVia: item.pickedVia,
          weight: item.weight,
        })
      }
    }

    const nextPage = payload.pageData?.nextPage
    if (nextPage == null) break
    page = nextPage
  }

  return itemDetailsByItemId
}

// One item to refund or credit. `refundQty` may be fractional for weighted items.
export interface ItemAppeasementLineInput {
  // Exact digit string; sent as an unquoted JSON integer (see createExactIntJsonEncoder).
  orderItemId: string
  refundQty: number
  refundReasonId: number
}

// Serializes JSON keeping snowflake ids (> 2^53) as unquoted integer literals — a JS Number rounds them
// and Rails matches integer id columns. Wrap ids with exactInt(), then encode(). Mirrors submitRedelivery.
function createExactIntJsonEncoder(): {
  exactInt: (rawDigits: string) => string
  encode: (value: unknown) => string
} {
  const placeholders = new Map<string, string>()
  const exactInt = (rawDigits: string): string => {
    const token = `__EXACT_INT_${placeholders.size}__`
    placeholders.set(token, rawDigits)
    return token
  }
  const encode = (value: unknown): string => {
    let body = JSON.stringify(value)
    for (const [token, rawDigits] of placeholders) {
      body = body.replace(`"${token}"`, rawDigits)
    }
    return body
  }
  return { exactInt, encode }
}

// One item in an item-level redelivery cart. Each row carries its own item-specific order_issue_type_id
// (the discriminator that makes the redelivery item-level rather than delivery-level).
export interface ItemRedeliveryCartItem {
  // Exact digit string (snowflake > 2^53); sent as an unquoted JSON integer (see createExactIntJsonEncoder).
  orderItemId: string
  // May be fractional for weighted items; preserved because order_item_id is an unquoted int (else the
  // controller runs recast_cart_values -> qty.to_i and truncates fractional qty to 0).
  qty: number
  // The item-specific OrderIssueType id (from the refund-items reason dropdown). Its presence per-item,
  // with NO top-level order_issue_type_id, is what makes the controller treat this as item-level.
  orderIssueTypeId: number
}

// POST an ITEM-LEVEL redelivery via the same endpoint as submitRedelivery. Discriminator vs the
// delivery-level path: NO top-level order_issue_type_id, and each cart row carries its own
// order_issue_type_id; the controller then re-checks IC+ membership before offering item redelivery.
// `use_replacement` is false: item_issues carries no substitute data, so we redeliver the originally
// ordered item (a conservative default; the substitute toggle can be added later). Mirrors
// submitRedelivery's exact-int JSON + X-CSRF-Token transport (the endpoint needs raw JSON to preserve
// snowflake ids and fractional weights).
export async function submitItemRedelivery({
  orderId,
  cart,
  deliveryOptionId,
  reason,
  caseNumber,
}: {
  orderId: string
  cart: ItemRedeliveryCartItem[]
  deliveryOptionId: string
  reason: string
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const { exactInt, encode } = createExactIntJsonEncoder()
  const body = encode({
    id: orderId,
    cart: cart.map(item => ({
      order_item_id: exactInt(item.orderItemId),
      use_replacement: false,
      qty: item.qty,
      // Per-item order_issue_type_id (with no top-level one) => item-level redelivery (IC+ re-checked).
      order_issue_type_id: item.orderIssueTypeId,
    })),
    create_order_issues: true,
    deliveries: [{ delivery_option_id: exactInt(deliveryOptionId), price: 0 }],
    reason,
    description: '',
    // Send both: attribution derives from case_number via grab_ticket_id, else the appeasement lands
    // with no ticket. Mirrors the care_create path.
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: RESOLVE_ITEM_ISSUE_UI_ORIGIN,
  })

  const url = new URL(`/admin/orders/${orderId}/redelivery`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  if (res.status === 422) {
    // 422 = validation error (JSON) or expired CSRF (HTML). Prefer the message; else re-fetch the token.
    const message = await readRedeliveryError(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readRedeliveryError(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// PUT an item-level refund to the original method (legacy care_refund, refund_type individual_items).
// Route :id is the OrderDelivery; per-item amounts are computed server-side.
export async function submitResolveItemIssue({
  orderDeliveryId,
  items,
  caseNumber,
}: {
  orderDeliveryId: string
  items: ItemAppeasementLineInput[]
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const { exactInt, encode } = createExactIntJsonEncoder()
  // Rails nested-attrs hash keyed by string index. refund_qty stays a number so fractional weights survive.
  const orderItemsAttributes: Record<string, unknown> = {}
  items.forEach((item, index) => {
    orderItemsAttributes[String(index)] = {
      id: exactInt(item.orderItemId),
      refund_qty: item.refundQty,
      refund_reason_id: item.refundReasonId,
    }
  })

  const body = encode({
    id: orderDeliveryId,
    refund_type: 'individual_items',
    order_delivery: { order_items_attributes: orderItemsAttributes },
    // Both sent; the server reads one per feature flag.
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: RESOLVE_ITEM_ISSUE_UI_ORIGIN,
  })

  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/care_refund`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'PUT',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// POST an item-level store credit (legacy care credit). Route :id is the Order; customer_id shard-scopes.
export async function submitItemCredit({
  orderId,
  customerId,
  items,
  caseNumber,
}: {
  orderId: string
  customerId: string
  items: ItemAppeasementLineInput[]
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const { exactInt, encode } = createExactIntJsonEncoder()
  const body = encode({
    id: orderId,
    customer_id: customerId,
    credits: {
      order_items: items.map(item => ({
        id: exactInt(item.orderItemId),
        qty: item.refundQty,
        refund_reason_id: item.refundReasonId,
      })),
      issuable_id: null,
      issuable_type: 'OrderItem',
    },
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: RESOLVE_ITEM_ISSUE_UI_ORIGIN,
  })

  const url = new URL(`/admin/api/v1/care/orders/${orderId}/credit`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// UI-origin tags the legacy "Poor service credits" flow records for auditing. The bento variant
// swaps the tag; the server reads it to route the credit (both values are in `care_create`'s
// cheddar-eligible allowlist).
const POOR_SERVICE_UI_ORIGIN = 'care_admin_poor_service_credits'
const POOR_SERVICE_BENTO_UI_ORIGIN = 'care_admin_bento_poor_service_credits'
// coupon[coupon_type] recorded by the legacy couponsAdapter for every poor-service option.
const POOR_SERVICE_COUPON_TYPE = 'CS: Bad Experience'

// The four poor-service credit options, in legacy render order (credit15 renders above credit5).
export type PoorServiceCreditOption = 'credit15' | 'credit5' | 'freeDelivery' | 'freeMonthOfICPlus'

// Availability from the legacy appeasement_permissions endpoint. Key PRESENCE matters and is
// preserved: `credit15` is present only for the bento variant_15 segment, and the RD-SFP / rescue
// branches omit keys entirely. `null` (key absent -> option hidden) is distinct from `false`
// (present but unavailable -> option shown greyed/disabled).
export interface PoorServiceCreditAvailability {
  credit15: boolean | null
  credit5: boolean | null
  freeDelivery: boolean | null
  freeMonthOfICPlus: boolean | null
  // Not options: drive the extra hide rules and the ui_origin bento swap.
  sfxRestricted: boolean
  bentoPhase2Variant: string | null
}

// GET request (safe verb), so no CSRF token is required. Mirrors getOrderRefundableCharges.
export async function getPoorServiceCreditOptions({
  orderId,
  customerId,
}: {
  orderId: string
  customerId: string
}): Promise<PoorServiceCreditAvailability> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  // Collection action: the order id is a query param, not a path segment.
  const url = new URL('/admin/api/v1/care/orders/appeasement_permissions', baseUrl)
  url.searchParams.set('id', orderId)
  url.searchParams.set('customer_id', customerId)

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }

  const json = await res.json()
  const poorService = json?.data?.poorService ?? {}
  // Only surface a boolean when the key is actually present, so absent keys stay `null` and the
  // modal hides those options (matching the legacy AppeasementModalBody, which iterates the keys).
  const flag = (key: string): boolean | null =>
    typeof poorService[key] === 'boolean' ? poorService[key] : null
  return {
    credit15: flag('credit15'),
    credit5: flag('credit5'),
    freeDelivery: flag('freeDelivery'),
    freeMonthOfICPlus: flag('freeMonthOfICPlus'),
    sfxRestricted: poorService.sfxRestricted === true,
    bentoPhase2Variant:
      typeof poorService.bentoPhase2Variant === 'string' ? poorService.bentoPhase2Variant : null,
  }
}

// Upper bound on how long the caution lookup may run before we give up and fail open. Because the
// appeasement modals keep submit disabled while this lookup is pending (so an about-to-appear warning
// can't be skipped), a hung request would otherwise disable submit indefinitely. Aborting after this
// budget lets the primary flow proceed rather than stranding the agent.
export const CAUTION_LOOKUP_TIMEOUT_MS = 8000

// GET request (safe verb), so no CSRF token is required. Mirrors getOrderRefundableCharges.
//
// Collection-route read (no :id path segment) behind the legacy appeasement-abuse guard: it is
// gated server-side by the `appeasements_prompt_agent_if_user_blocked` Roulette flag, the
// `see_appeasement_fraud_warning` permission, and a CareTools `get_high_precision_blocks` lookup.
// When it returns true the appeasement modals must show a mandatory acknowledgment before the agent
// can submit. This is an advisory nudge layered on top of server-side fraud enforcement (no request
// body field is sent), so any failure — flag off (404), EU-restricted (403), or a downstream error —
// falls back to `false` (no warning) rather than blocking the primary appeasement flow. A missing
// `shouldCautionAgentAgainstAppeasement` key likewise defaults to false.
export async function getShouldCautionAgentAgainstAppeasement({
  orderId,
  customerId,
}: {
  orderId: string
  customerId: string
}): Promise<boolean> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  // Collection action: order/customer ids are query params, not path segments.
  const url = new URL('/admin/api/v1/care/orders/should_caution_agent_against_appeasement', baseUrl)
  url.searchParams.set('order_id', orderId)
  url.searchParams.set('customer_id', customerId)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), CAUTION_LOOKUP_TIMEOUT_MS)
  try {
    const res = await cxAdminFetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      mode: 'cors',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })

    // Fail open: the flag being off returns an empty-bodied 404, which is the common case, not an
    // error worth surfacing. Treat any non-ok response as "no caution applies".
    if (!res.ok) return false

    const json = await res.json().catch(() => null)
    return json?.shouldCautionAgentAgainstAppeasement === true
  } catch (err) {
    // A timeout abort fails open (same as any transport error). Re-throw genuine network errors so
    // the hook still surfaces them via its `error` field.
    if (controller.signal.aborted) return false
    throw err
  } finally {
    clearTimeout(timeoutId)
  }
}

// Mirrors the legacy couponsAdapter COUPONS_MAP: the coupon[value]/discount_category_id and the
// delivery/express flags per option. `value` is null for the delivery/express paths, where the
// server derives the amount (and drops coupon[value] entirely).
interface PoorServiceCouponSpec {
  value: number | null
  discountCategoryId: number
  deliveryCoupon?: boolean
  expressCoupon?: boolean
}
const POOR_SERVICE_COUPONS: Record<PoorServiceCreditOption, PoorServiceCouponSpec> = {
  credit15: { value: 15, discountCategoryId: 11 },
  credit5: { value: 5, discountCategoryId: 11 },
  freeDelivery: { value: null, discountCategoryId: 9, deliveryCoupon: true },
  freeMonthOfICPlus: { value: null, discountCategoryId: 9, expressCoupon: true },
}

// POST a poor-service credit to the legacy `coupons#care_create` endpoint. care_create branches on
// express_coupon -> delivery_coupon -> cheddar-credit (ui_origin allowlist) to pick the service.
// Sent as Rails bracket-nested form fields, not JSON, so ParamsWrapper can't clobber the `coupon`
// nesting.
export async function submitPoorServiceCredit({
  orderId,
  customerId,
  option,
  caseNumber,
  bentoPhase2Variant,
}: {
  orderId: string
  customerId: string
  option: PoorServiceCreditOption
  caseNumber: string
  bentoPhase2Variant: string | null
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const spec = POOR_SERVICE_COUPONS[option]
  // The bento variant_5/variant_15 segments route to the bento-specific ui_origin (mirrors the
  // legacy useAppeasementsManager). Both tags are in care_create's cheddar-eligible allowlist.
  const uiOrigin =
    bentoPhase2Variant === 'variant_5' || bentoPhase2Variant === 'variant_15'
      ? POOR_SERVICE_BENTO_UI_ORIGIN
      : POOR_SERVICE_UI_ORIGIN

  const body = new URLSearchParams({
    'coupon[ticket_id]': caseNumber,
    'coupon[user_id]': customerId,
    'coupon[coupon_type]': POOR_SERVICE_COUPON_TYPE,
    'coupon[discount_category_id]': String(spec.discountCategoryId),
    'coupon[cause_order_id]': orderId,
    'coupon[ui_origin]': uiOrigin,
    // care_create validates this top-level `case_number` first, and (with the case-number roulette
    // on) grab_ticket_id reads it as the ReturnValueService ticket for the cheddar-credit path.
    case_number: caseNumber,
  })
  // Omit coupon[value] on the delivery/express paths; the cheddar path reads it for credit5/credit15.
  if (spec.value != null) body.set('coupon[value]', String(spec.value))
  if (spec.deliveryCoupon) body.set('coupon[delivery_coupon]', 'true')
  if (spec.expressCoupon) body.set('coupon[express_coupon]', 'true')

  const url = new URL(`/admin/customers/${customerId}/care_coupon`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrfToken,
    },
    body,
  })

  if (res.status === 422) {
    // 422 is either a controller validation error (JSON `{errors}`) or an expired CSRF token (an
    // HTML error page). Prefer the validation message; otherwise clear the cached token to re-fetch.
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// ---------------------------------------------------------------------------
// Order charges panel (legacy OrderChargesUI): per-fee refund/waive, full
// "Total charge" refund, order-total refund, and the FDO retailer-discount
// credit. All read from the same `/orders/:id/charges` endpoint that
// getOrderRefundableCharges uses; these transports write the actions back.
// ---------------------------------------------------------------------------

// ui_origin stamped on every order-charges write (legacy UI_ORIGIN.careAdminOrderCharges).
const ORDER_CHARGES_UI_ORIGIN = 'care_admin_order_charges'

// Legacy refund_type / issuable_type discriminators used to route a row to the correct transport.
export const RETAILER_DISCOUNT_CREDIT_REFUND_TYPE = 'retailer_discount_credit'
export const ORDER_TOTAL_REFUND_TYPE = 'order_total'
export const WHOLE_REFUND_TYPE = 'whole'

// A tax (or tax-like) charge the legacy reconciliation table lists on its own row alongside a fee.
// `label` and `amount` are the server's pre-formatted strings, surfaced verbatim so the tax-exclusive
// fee `amount` reconciles with the fee + tax a refund actually returns.
export interface OrderChargeTaxLine {
  label: string
  amount: string
}

// A single actionable charge row from the legacy /charges response.
export interface OrderChargeRow {
  // Server-provided display label (e.g. "Service fee", "Total charge", "Credit Retailer Discount (FDO)").
  description: string
  // Pre-formatted money string from the legacy API (e.g. "$4.99"); empty when unavailable. For fees
  // this is the tax-exclusive base amount (the refund still returns fee + tax; see `taxLines`).
  amount: string
  // Tax sub-line(s) charged alongside this fee, pulled from reconciliationDetails (see
  // resolveFeeTaxLines). Empty for fees with no tax row and for whole-order refunds / the FDO credit
  // (those already fold tax into `amount`). The panel renders each as a muted sub-line so the base
  // `amount` is not mistaken for the full charge. Optional: only `getOrderCharges` fee rows populate
  // it; hand-built rows may omit it (undefined behaves like []).
  taxLines?: OrderChargeTaxLine[]
  // Legacy refund_type / issuable_type discriminator: a fee slug ("service_fee", "bags_fee",
  // "late_cancel_fee", ...), "whole", "order_total", or "retailer_discount_credit". Drives which
  // transport the confirm modal calls.
  refundType: string
  // Whether the agent may act on this row now (fees: isRefundable; refunds/credits: allowed).
  actionable: boolean
  // "Refunded" | "Waived" | "Credited" when the action was already applied, else null.
  reconciliationStatus: string | null
}

export interface OrderCharges {
  // feeSummary.type: "Refund" when order refunds are allowed for this order, else "Waive". Labels the
  // per-fee action button.
  feeActionType: string
  // Per-fee rows (feeSummary.fees), in server order.
  fees: OrderChargeRow[]
  // Whole-delivery refund ("Total charge", refund_type "whole", via care_refund). Null if absent.
  fullRefund: OrderChargeRow | null
  // Order-total refund (refund_type "order_total", via refund?refund_type=whole). Only rendered when
  // orderTotalRefundEnabled.
  orderTotalRefund: OrderChargeRow | null
  orderTotalRefundEnabled: boolean
  // FDO retailer-discount credit (additionalActions, refund_type "retailer_discount_credit"). Null
  // when the cx_full_refund_order_issue flag suppresses additionalActions or none applies.
  retailerDiscountCredit: OrderChargeRow | null
  // Numeric order-delivery id parsed from links.refund; used to build the care_refund / refund URLs.
  orderDeliveryId: string | null
}

interface RawOrderChargeRow {
  description?: string
  amount?: string
  refundType?: string
  isRefundable?: boolean
  allowed?: boolean
  reconciliationStatus?: string | null
}

// One reconciliationDetails row: a labeled line with one value column per reconciliation snapshot
// (["", "Current"] or ["", "Initial", "Current"]); the current figure is always the LAST column.
interface RawReconciliationRow {
  label?: { value?: string } | null
  values?: Array<{ value?: string } | null> | null
}

// reconciliationDetails lists each fee's tax on its own row, but the label rarely equals
// `${feeDescription} tax`: service-tax rows carry a "(<service tax type>)" suffix (order_presenter
// amount_description) and a couple of fees use a custom tax label. Map fee refund_type -> the tax row
// label prefix(es) to look for; any fee not listed falls back to `${description} tax`.
const FEE_TAX_ROW_LABEL_PREFIXES: Record<string, string[]> = {
  heavy_delivery_fee: ['Heavy order fee tax'],
  regulatory_shopper_wage_guarantee_fee: ['Regulatory response fee tax'],
}

const normalizeReconLabel = (label: string): string =>
  label.trim().replace(/\s+/g, ' ').toLowerCase()

// Pull the tax sub-line(s) for a fee out of the reconciliationDetails rows. A row matches when its
// label equals the expected tax prefix or begins with `${prefix} (` (the service-tax-type suffix).
// The current figure is the LAST value column; zero/empty amounts are dropped server-side, so every
// match is a real, non-zero tax charge. Whole-order refunds / the FDO credit pass no rows here.
function resolveFeeTaxLines(
  fee: OrderChargeRow,
  reconRows: RawReconciliationRow[]
): OrderChargeTaxLine[] {
  const prefixes = (FEE_TAX_ROW_LABEL_PREFIXES[fee.refundType] ?? [`${fee.description} tax`]).map(
    normalizeReconLabel
  )

  const taxLines: OrderChargeTaxLine[] = []
  for (const row of reconRows) {
    const rawLabel = row?.label?.value
    if (!rawLabel) continue
    const normalized = normalizeReconLabel(rawLabel)
    const matches = prefixes.some(
      prefix => normalized === prefix || normalized.startsWith(`${prefix} (`)
    )
    if (!matches) continue

    const values = row?.values ?? []
    const amount = values[values.length - 1]?.value
    if (!amount) continue

    taxLines.push({ label: rawLabel, amount })
  }
  return taxLines
}

// Normalize a raw fee/refund/credit row. Fees expose `isRefundable`; refunds and additionalActions
// expose `allowed` — `actionableKey` selects which flag gates the action.
function toOrderChargeRow(
  raw: RawOrderChargeRow | null | undefined,
  actionableKey: 'isRefundable' | 'allowed'
): OrderChargeRow | null {
  if (!raw) return null
  return {
    description: raw.description ?? '',
    amount: raw.amount ?? '',
    refundType: raw.refundType ?? '',
    actionable: Boolean(raw[actionableKey]),
    reconciliationStatus: raw.reconciliationStatus ?? null,
  }
}

// GET the full order-charges payload (fees, whole/order-total refunds, FDO credit). GET is a safe
// verb, so no CSRF token is required. The server applies every gate (Roulette flags, order_refunds?,
// can_refund_cancellation_fee?) and returns only rows the agent may act on.
export async function getOrderCharges({
  orderId,
  customerId,
}: {
  orderId: string
  customerId: string
}): Promise<OrderCharges> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL(`/admin/api/v1/care/orders/${orderId}/charges`, baseUrl)
  url.searchParams.set('customer_id', customerId)

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  })

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }

  const json = await res.json()

  // reconciliationDetails carries each fee's tax on its own row; attach the matching tax sub-line(s)
  // to each fee below so the tax-exclusive feeSummary amount reconciles with the fee + tax a refund
  // returns. (Whole-order refunds / the FDO credit already fold tax into their amount, so they skip this.)
  const reconRows = (json?.reconciliationDetails?.rows ?? []) as RawReconciliationRow[]

  // feeSummary.fees is a keyed object ({ serviceFee: {...}, bagsFee: {...} }); flatten to an array.
  const rawFees = (json?.feeSummary?.fees ?? {}) as Record<string, RawOrderChargeRow>
  const fees = Object.values(rawFees)
    .map(fee => toOrderChargeRow(fee, 'isRefundable'))
    .filter((row): row is OrderChargeRow => row !== null)
    .map(fee => ({ ...fee, taxLines: resolveFeeTaxLines(fee, reconRows) }))

  // additionalActions is an array; the FDO retailer-discount credit is the row whose refund_type
  // matches. (Suppressed to [] when the cx_full_refund_order_issue flag is on.)
  const additionalActions = (json?.additionalActions ?? []) as RawOrderChargeRow[]
  const retailerDiscountCredit = toOrderChargeRow(
    additionalActions.find(a => a?.refundType === RETAILER_DISCOUNT_CREDIT_REFUND_TYPE) ?? null,
    'allowed'
  )

  return {
    feeActionType: json?.feeSummary?.type ?? 'Refund',
    fees,
    fullRefund: toOrderChargeRow(json?.refunds?.fullRefund, 'allowed'),
    orderTotalRefund: toOrderChargeRow(json?.refunds?.orderTotalRefund, 'allowed'),
    orderTotalRefundEnabled: Boolean(json?.orderTotalRefundEnabled),
    retailerDiscountCredit,
    orderDeliveryId: parseOrderDeliveryId(json?.links?.refund ?? null),
  }
}

// PUT a per-fee refund/waive or a whole-delivery ("Total charge") refund to the legacy care_refund
// endpoint. `refundType` is the fee slug (e.g. "service_fee", "bags_fee", "late_cancel_fee") or
// "whole". Refund-vs-waive is decided server-side (money back if already charged, else the uncharged
// fee is zeroed). Mirrors submitResolveItemIssue's CORS-simple, `_method=put`, disambiguated-422 shape.
export async function submitFeeRefund({
  orderDeliveryId,
  refundType,
  caseNumber,
}: {
  orderDeliveryId: string
  refundType: string
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const body = new URLSearchParams({
    _method: 'put',
    authenticity_token: csrfToken,
    id: orderDeliveryId,
    refund_type: refundType,
    // Both sent; the server reads one per feature flag.
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: ORDER_CHARGES_UI_ORIGIN,
  })

  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/care_refund`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body,
  })

  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// PUT an order-total refund to the legacy `refund` endpoint. The URL carries `?refund_type=whole`
// (the fixed route-helper arg on the server's orderTotalRefund link). Rails merges query over body
// (`request_parameters.merge(query_parameters)`), so the effective refund_type is the valid "whole"
// even though the body sends "order_total" for parity — "order_total" is NOT in
// OrderDeliveries::RefundService::REFUND_TYPES and would raise if it reached the service. `no_redirect`
// makes process_refund return 204 instead of an HTML redirect.
export async function submitOrderChargeRefund({
  orderDeliveryId,
  refundType,
  caseNumber,
}: {
  orderDeliveryId: string
  refundType: string
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const body = new URLSearchParams({
    _method: 'put',
    authenticity_token: csrfToken,
    id: orderDeliveryId,
    refund_type: refundType,
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: ORDER_CHARGES_UI_ORIGIN,
    no_redirect: 'true',
  })

  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/refund`, baseUrl)
  // Load-bearing: the query refund_type wins over the body, resolving to the valid "whole" refund.
  url.searchParams.set('refund_type', WHOLE_REFUND_TYPE)

  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body,
  })

  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// POST the FDO retailer-discount credit (legacy createCredit). Route :id is the Order; customer_id
// shard-scopes. `credits[issuable_type]` is the row's refund_type ("retailer_discount_credit"); there
// is no per-item array (unlike submitItemCredit). Real POST, no `_method`.
export async function submitRetailerDiscountCredit({
  orderId,
  customerId,
  refundType,
  caseNumber,
}: {
  orderId: string
  customerId: string
  refundType: string
  caseNumber: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const body = new URLSearchParams({
    id: orderId,
    customer_id: customerId,
    case_number: caseNumber,
    ticket_id: caseNumber,
    ui_origin: ORDER_CHARGES_UI_ORIGIN,
    authenticity_token: csrfToken,
    'credits[issuable_type]': refundType,
  })

  const url = new URL(`/admin/api/v1/care/orders/${orderId}/credit`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    redirect: 'follow',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body,
  })

  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// ---------------------------------------------------------------------------
// Order Issues (mirror of the legacy /care "Order Issues" card)
//
// Reads and writes go through the v3 `api/v1/order_issues` JSON endpoints (the `/admin/api/v1`
// namespace defaults to JSON, so no `.json` extension and no HTML 302). Writes tunnel the verb via
// `_method` and carry the CSRF token as a body `authenticity_token` field, which keeps every request
// a CORS-simple request (no preflight) — the same pattern as `updateOrderDeliveryAddress` /
// `sendAppeasementEmail`.
//
// Multi-tenant sharding: the base controller's `set_multi_tenant` around_action wraps
// `find_order_issue`, so `OrderIssue.find(id)` for update/destroy only resolves on the right shard
// when `customer_id` is supplied. It's sent on the read too as a harmless direct shard hint.
// ---------------------------------------------------------------------------

export interface OrderIssueType {
  id: string
  name: string | null
  category: string | null
  itemSpecific: boolean
}

export interface OrderIssueOrderItem {
  id: string
  item: { displayName: string | null } | null
}

export interface OrderIssue {
  id: string
  creatorName: string | null
  warehouseName: string | null
  automationCompletedAt: string | null
  automationSetAt: string | null
  orderIssueType: OrderIssueType
  orderItem: OrderIssueOrderItem | null
}

// The endpoint returns snake_case; normalize each element into the camelCase `OrderIssue` shape the
// UI consumes. Defensive about missing/null nested objects so a malformed row can't crash the table.
function parseOrderIssue(raw: unknown): OrderIssue {
  const row = (raw ?? {}) as Record<string, unknown>
  const rawType = (row.order_issue_type ?? {}) as Record<string, unknown>
  const rawOrderItem = row.order_item as Record<string, unknown> | null | undefined

  let orderItem: OrderIssueOrderItem | null = null
  if (rawOrderItem && rawOrderItem.id != null) {
    const rawItem = rawOrderItem.item as Record<string, unknown> | null | undefined
    orderItem = {
      id: String(rawOrderItem.id),
      item: rawItem ? { displayName: (rawItem.display_name as string | null) ?? null } : null,
    }
  }

  return {
    id: String(row.id ?? ''),
    creatorName: (row.creator_name as string | null) ?? null,
    warehouseName: (row.warehouse_name as string | null) ?? null,
    automationCompletedAt: (row.automation_completed_at as string | null) ?? null,
    automationSetAt: (row.automation_set_at as string | null) ?? null,
    orderIssueType: {
      id: String(rawType.id ?? ''),
      name: (rawType.name as string | null) ?? null,
      category: (rawType.category as string | null) ?? null,
      itemSpecific: Boolean(rawType.item_specific),
    },
    orderItem,
  }
}

// GET request (safe verb), so no CSRF token is required. `order_id` scopes the list; `customer_id`
// is a direct shard hint (see the sharding note above).
export async function fetchOrderIssues({
  orderId,
  customerId,
}: {
  orderId: string
  customerId: string
}): Promise<OrderIssue[]> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL('/admin/api/v1/order_issues', baseUrl)
  url.searchParams.set('order_id', orderId)
  url.searchParams.set('customer_id', customerId)

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  })

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }

  const json = await res.json()
  if (!Array.isArray(json)) return []
  return json.map(parseOrderIssue)
}

// PUT (tunneled) /admin/api/v1/order_issues/:id — recategorizes an issue by setting a new
// `order_issue_type_id` (and, for multi-delivery orders, the target `order_delivery_id`). api/v1
// params are top-level, NOT nested under `order_issue[...]`.
export async function updateOrderIssue({
  orderIssueId,
  orderIssueTypeId,
  orderDeliveryId,
  customerId,
}: {
  orderIssueId: string
  orderIssueTypeId: number
  orderDeliveryId?: string
  customerId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  if (!orderIssueId) throw new Error('Missing order issue id')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const body = new URLSearchParams({
    _method: 'put',
    authenticity_token: csrfToken,
    customer_id: customerId,
    order_issue_type_id: String(orderIssueTypeId),
  })
  if (orderDeliveryId) body.set('order_delivery_id', orderDeliveryId)

  const url = new URL(`/admin/api/v1/order_issues/${orderIssueId}`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body,
  })

  if (res.status === 422) {
    // 422 is ambiguous: a controller validation error (JSON `{errors}`) or an expired CSRF token (an
    // HTML error page). Prefer the validation message; otherwise clear the cached token to re-fetch.
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// DELETE (tunneled) /admin/api/v1/order_issues/:id — soft-deletes the issue. `destroy` returns
// `head :ok` with no body.
export async function deleteOrderIssue({
  orderIssueId,
  customerId,
}: {
  orderIssueId: string
  customerId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  if (!orderIssueId) throw new Error('Missing order issue id')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/api/v1/order_issues/${orderIssueId}`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({
      _method: 'delete',
      authenticity_token: csrfToken,
      customer_id: customerId,
    }),
  })

  if (res.status === 422) {
    const message = await readCxAdminErrors(res)
    if (message) throw new Error(message)
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    const message = await readCxAdminErrors(res)
    throw new Error(message ?? `Request failed (${res.status})`)
  }
}

// POST /admin/order_deliveries/:id/send_late_notification.json (PUT-tunneled)
// Creates a new delivery ETA (DeliveryPromise) and sends the "running late" email to the customer.
// Modeled on submitAddCharge (PL-69706): a CORS-simple POST with the CSRF token in
// `authenticity_token` (no OPTIONS preflight). The Rails route is a PUT, so we tunnel it via
// `_method=put`. `.json` forces the controller's `format.json` branch ({ success, message });
// without it `format.html` 302-redirects on success too, and the followed HTML page makes
// res.json() throw, surfacing success as a failure toast. Decide the outcome from the parsed
// body's `success`, never `res.ok` after a followed redirect. `startAt`/`endAt` are ISO 8601
// instants; the controller runs `Time.parse` on each.
export async function sendLateNotification({
  orderDeliveryId,
  startAt,
  endAt,
}: {
  orderDeliveryId: string
  startAt: string
  endAt: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(
    `/admin/order_deliveries/${orderDeliveryId}/send_late_notification.json`,
    baseUrl
  )
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({
      _method: 'put',
      authenticity_token: csrfToken,
      'delivery_promise[start_at]': startAt,
      'delivery_promise[end_at]': endAt,
      'delivery_promise[source]': 'admin',
    }),
  })

  // Body, not status, decides the outcome. Parse first so a 422 with a real message isn't misread
  // as an expired session.
  let json: { success?: unknown; message?: unknown } | null = null
  try {
    json = (await res.json()) as { success?: unknown; message?: unknown }
  } catch {
    // No JSON body (e.g. a followed HTML redirect).
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    // Bad/expired CSRF token: Rails raises InvalidAuthenticityToken (422, HTML). Re-fetch next time.
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(
      typeof json?.message === 'string' ? json.message : `Request failed (${res.status})`
    )
  }

  if (!json || typeof json.success !== 'boolean') {
    throw new Error('Send late email failed: unexpected response')
  }
  if (!json.success) {
    throw new Error(
      typeof json.message === 'string'
        ? json.message
        : 'Failed to send late email. Please try again.'
    )
  }
}

// GET /admin/orders/:id/receipt_link.json — returns the admin receipt link for an order.
// Safe GET verb, so no CSRF token is required. Fails open to null on any error so the
// receipt link section degrades gracefully rather than blocking the order detail page.
export async function fetchOrderReceiptUrl({
  orderId,
}: {
  orderId: string
}): Promise<string | null> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl) return null

  const url = new URL(`/admin/orders/${orderId}/receipt_link.json`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
    headers: { Accept: 'application/json' },
  })

  if (!res.ok) return null

  try {
    const json = (await res.json()) as { success?: boolean; receipt_url?: string }
    return typeof json.receipt_url === 'string' ? json.receipt_url : null
  } catch {
    return null
  }
}

// POST /admin/order_deliveries/:id/send_receipt_email.json
// Emails a digital receipt to the shopper (fire-and-forget on the backend). Modeled on
// submitAddCharge (PL-69706): a CORS-simple POST with the CSRF token in `authenticity_token`; this
// is a native POST route so there is no `_method` tunnel. `.json` forces the `format.json` branch;
// the outcome is decided from the parsed body's `success`, never `res.ok` after a followed redirect.
export async function sendReceiptEmail({
  orderDeliveryId,
}: {
  orderDeliveryId: string
}): Promise<void> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const csrfToken = await fetchCxAdminCsrfToken()
  if (!csrfToken) throw new Error('Could not get cx admin session. Are you logged in?')

  const url = new URL(`/admin/order_deliveries/${orderDeliveryId}/send_receipt_email.json`, baseUrl)
  const res = await cxAdminFetch(url.toString(), {
    method: 'POST',
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: new URLSearchParams({
      authenticity_token: csrfToken,
    }),
  })

  let json: { success?: unknown; message?: unknown } | null = null
  try {
    json = (await res.json()) as { success?: unknown; message?: unknown }
  } catch {
    // No JSON body (e.g. a followed HTML redirect).
  }

  if (res.status === 422 && (!json || typeof json.message !== 'string')) {
    cachedCsrfToken = null
    throw new Error('Session expired. Please refresh and try again.')
  }

  if (!res.ok) {
    throw new Error(
      typeof json?.message === 'string' ? json.message : `Request failed (${res.status})`
    )
  }

  if (!json || typeof json.success !== 'boolean') {
    throw new Error('Email receipt failed: unexpected response')
  }
  if (!json.success) {
    throw new Error(
      typeof json.message === 'string'
        ? json.message
        : 'Failed to email the receipt. Please try again.'
    )
  }
}

// ---------------------------------------------------------------------------
// Legacy fast-find search: shoppers and applicants
// ---------------------------------------------------------------------------

export interface ShopperSearchResult {
  id: string
  name: string
  email: string | null
  regionId: number | null
}

// GET /admin/api/v1/drivers/search — searches shoppers via Hub RPC to the shoppers service.
export async function searchShoppers(query: string): Promise<ShopperSearchResult[]> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL('/admin/api/v1/drivers/search', baseUrl)
  url.searchParams.set('query', query)
  url.searchParams.set('per', '20')

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  })

  if (!res.ok) throw new Error(`Request failed (${res.status})`)

  const json = await res.json()
  return (json?.data ?? []).map(
    (d: { id: string; name: string; email?: string | null; region_id?: number | null }) => ({
      id: d.id,
      name: d.name,
      email: d.email ?? null,
      regionId: d.region_id ?? null,
    })
  )
}

export interface ApplicantSearchResult {
  id: string
  name: string
  email: string | null
  regionId: number | null
}

// GET /admin/api/v1/applicants/search — searches applicants via Hub RPC to the shoppers service.
export async function searchApplicants(query: string): Promise<ApplicantSearchResult[]> {
  const baseUrl = clientEnv.PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL
  if (!baseUrl)
    throw new Error('cx admin URL is not configured (PUBLIC_CLIENT_CX_ADMIN_MIGRATION_BASE_URL)')

  const url = new URL('/admin/api/v1/applicants/search', baseUrl)
  url.searchParams.set('query', query)
  url.searchParams.set('per', '20')

  const res = await cxAdminFetch(url.toString(), {
    method: 'GET',
    credentials: 'include',
    mode: 'cors',
  })

  if (!res.ok) throw new Error(`Request failed (${res.status})`)

  const json = await res.json()
  return (json?.data ?? []).map(
    (d: { id: number; name: string; email?: string | null; region_id?: number | null }) => ({
      id: String(d.id),
      name: d.name,
      email: d.email ?? null,
      regionId: d.region_id ?? null,
    })
  )
}
